/**
 * About Page Section Block Templates
 *
 * Pre-designed section blocks for the about page builder.
 * Designers pick from these to assemble their about page from scratch.
 * Each block is self-contained with responsive Tailwind CSS.
 */

const PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 600'%3E%3Crect fill='%23e2e8f0' width='800' height='600'/%3E%3Ctext fill='%2394a3b8' font-family='system-ui' font-size='20' text-anchor='middle' dominant-baseline='middle' x='400' y='300'%3EClick to add image%3C/text%3E%3C/svg%3E";
const PH_SQ = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 400'%3E%3Crect fill='%23e2e8f0' width='400' height='400'/%3E%3Ctext fill='%2394a3b8' font-family='system-ui' font-size='16' text-anchor='middle' dominant-baseline='middle' x='200' y='200'%3EAdd photo%3C/text%3E%3C/svg%3E";

const t = (content) => ({ type: 'text', content });

const aboutSectionBlocks = [

  // ═══════════════════════════════════════════════════════════════
  // 1. HERO — GRADIENT
  // ═══════════════════════════════════════════════════════════════
  {
    category: 'Hero',
    label: 'Hero — Gradient',
    description: 'Full-width hero with gradient background and centered text',
    block: {
      id: 'about_hero_gradient',
      type: 'section',
      enabled: true,
      structure: {
        type: 'section',
        className: 'relative min-h-[70vh] flex items-center justify-center overflow-hidden',
        style: { background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)' },
        children: [
          {
            type: 'div',
            className: 'absolute inset-0 opacity-20',
            style: {
              backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(99,102,241,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(168,85,247,0.2) 0%, transparent 50%)'
            },
            children: []
          },
          {
            type: 'div',
            className: 'relative z-10 max-w-4xl mx-auto text-center px-6 py-24',
            children: [
              {
                type: 'p',
                className: 'text-sm uppercase tracking-widest text-gray-400 mb-8 font-medium',
                style: { letterSpacing: '0.3em' },
                children: [t('About Us')]
              },
              {
                type: 'h1',
                className: 'text-5xl md:text-7xl font-bold text-white leading-tight mb-8',
                children: [t('Where Fashion Meets Purpose')]
              },
              {
                type: 'p',
                className: 'text-lg md:text-xl text-gray-300 max-w-2xl mx-auto leading-relaxed',
                children: [t('We craft pieces that tell stories, empower individuals, and celebrate the art of self-expression through thoughtful, intentional design.')]
              },
              {
                type: 'div',
                className: 'mt-12 mx-auto',
                style: { width: '48px', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)' },
                children: []
              }
            ]
          }
        ]
      }
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // 2. HERO — SPLIT LAYOUT
  // ═══════════════════════════════════════════════════════════════
  {
    category: 'Hero',
    label: 'Hero — Split Layout',
    description: 'Image and text side by side',
    block: {
      id: 'about_hero_split',
      type: 'section',
      enabled: true,
      structure: {
        type: 'section',
        className: 'grid grid-cols-1 lg:grid-cols-2 min-h-[60vh]',
        children: [
          {
            type: 'div',
            className: 'relative overflow-hidden bg-gray-100',
            children: [
              {
                type: 'img',
                className: 'w-full h-full object-cover min-h-[400px]',
                attributes: { src: PH, alt: 'Brand image' }
              }
            ]
          },
          {
            type: 'div',
            className: 'flex items-center px-8 lg:px-16 py-16 bg-white',
            children: [
              {
                type: 'div',
                className: 'max-w-lg',
                children: [
                  {
                    type: 'p',
                    className: 'text-sm uppercase tracking-wider text-indigo-500 font-semibold mb-4',
                    children: [t('Our Story')]
                  },
                  {
                    type: 'h1',
                    className: 'text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6',
                    children: [t('Designing for the Modern World')]
                  },
                  {
                    type: 'p',
                    className: 'text-lg text-gray-600 leading-relaxed mb-8',
                    children: [t('Founded with a vision to redefine contemporary fashion, we blend timeless craftsmanship with modern sensibility to create pieces that transcend seasons.')]
                  },
                  {
                    type: 'div',
                    className: 'flex items-center gap-4',
                    children: [
                      { type: 'div', className: 'w-12 h-0.5 bg-gray-900', children: [] },
                      {
                        type: 'span',
                        className: 'text-sm text-gray-500 font-medium',
                        children: [t('Since 2020')]
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // 3. STORY — NARRATIVE
  // ═══════════════════════════════════════════════════════════════
  {
    category: 'Story',
    label: 'Our Story',
    description: 'Image and narrative text with decorative quote mark',
    block: {
      id: 'about_story',
      type: 'section',
      enabled: true,
      structure: {
        type: 'section',
        className: 'py-20 md:py-28 bg-white',
        children: [
          {
            type: 'div',
            className: 'max-w-7xl mx-auto px-6',
            children: [
              {
                type: 'div',
                className: 'grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center',
                children: [
                  {
                    type: 'div',
                    className: 'relative',
                    children: [
                      {
                        type: 'img',
                        className: 'w-full rounded-2xl shadow-xl',
                        attributes: { src: PH, alt: 'Our story' }
                      },
                      {
                        type: 'div',
                        className: 'absolute -bottom-4 -right-4 w-24 h-24 bg-indigo-50 rounded-xl -z-10',
                        children: []
                      }
                    ]
                  },
                  {
                    type: 'div',
                    children: [
                      {
                        type: 'span',
                        className: 'text-7xl leading-none text-indigo-100 font-serif',
                        children: [t('\u201C')]
                      },
                      {
                        type: 'h2',
                        className: 'text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-6',
                        children: [t('The Story Behind Our Brand')]
                      },
                      {
                        type: 'p',
                        className: 'text-gray-600 leading-relaxed mb-4 text-lg',
                        children: [t('Every stitch tells a story. What began as a small studio with a handful of artisans has grown into a brand that celebrates the intersection of tradition and innovation.')]
                      },
                      {
                        type: 'p',
                        className: 'text-gray-600 leading-relaxed text-lg',
                        children: [t('Today, we continue to push boundaries while staying true to our roots \u2014 creating garments that make you feel as extraordinary as you are.')]
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // 4. PHILOSOPHY — STATEMENT
  // ═══════════════════════════════════════════════════════════════
  {
    category: 'Story',
    label: 'Brand Philosophy',
    description: 'Large statement text with minimal design',
    block: {
      id: 'about_philosophy',
      type: 'section',
      enabled: true,
      structure: {
        type: 'section',
        className: 'py-24 md:py-32 bg-gray-50',
        children: [
          {
            type: 'div',
            className: 'max-w-4xl mx-auto text-center px-6',
            children: [
              {
                type: 'p',
                className: 'text-sm uppercase tracking-widest text-gray-400 font-medium mb-8',
                style: { letterSpacing: '0.2em' },
                children: [t('Our Philosophy')]
              },
              {
                type: 'h2',
                className: 'text-3xl md:text-5xl font-bold text-gray-900 leading-tight mb-10',
                children: [t('Design is not just what it looks like and feels like. Design is how it works.')]
              },
              {
                type: 'div',
                className: 'w-16 h-px bg-gray-300 mx-auto mb-10',
                children: []
              },
              {
                type: 'p',
                className: 'text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed',
                children: [t('We believe that true luxury lies in the details \u2014 in the quality of materials, the precision of cuts, and the stories woven into every collection. Each piece is a testament to our commitment to excellence.')]
              }
            ]
          }
        ]
      }
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // 5. MISSION & VISION
  // ═══════════════════════════════════════════════════════════════
  {
    category: 'Story',
    label: 'Mission & Vision',
    description: 'Two cards showcasing mission and vision statements',
    block: {
      id: 'about_mission_vision',
      type: 'section',
      enabled: true,
      structure: {
        type: 'section',
        className: 'py-20 bg-white',
        children: [
          {
            type: 'div',
            className: 'max-w-6xl mx-auto px-6',
            children: [
              {
                type: 'h2',
                className: 'text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16',
                children: [t('What Drives Us')]
              },
              {
                type: 'div',
                className: 'grid grid-cols-1 md:grid-cols-2 gap-8',
                children: [
                  {
                    type: 'div',
                    className: 'p-10 bg-gray-50 rounded-2xl',
                    children: [
                      { type: 'span', className: 'text-3xl', children: [t('\uD83C\uDFAF')] },
                      { type: 'h3', className: 'text-2xl font-bold text-gray-900 mt-6 mb-4', children: [t('Our Mission')] },
                      { type: 'p', className: 'text-gray-600 leading-relaxed text-lg', children: [t('To create thoughtfully designed fashion that empowers individuals to express their authentic selves while championing sustainability and ethical craftsmanship.')] }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'p-10 bg-gray-50 rounded-2xl',
                    children: [
                      { type: 'span', className: 'text-3xl', children: [t('\u2728')] },
                      { type: 'h3', className: 'text-2xl font-bold text-gray-900 mt-6 mb-4', children: [t('Our Vision')] },
                      { type: 'p', className: 'text-gray-600 leading-relaxed text-lg', children: [t('To become the most loved conscious fashion brand \u2014 one that inspires change, celebrates diversity, and proves that beautiful design and responsibility can coexist.')] }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // 6. TEAM GRID
  // ═══════════════════════════════════════════════════════════════
  {
    category: 'Team',
    label: 'Team Members',
    description: 'Grid of team members with photos and roles',
    block: {
      id: 'about_team',
      type: 'section',
      enabled: true,
      structure: {
        type: 'section',
        className: 'py-20 bg-white',
        children: [
          {
            type: 'div',
            className: 'max-w-6xl mx-auto px-6',
            children: [
              {
                type: 'div',
                className: 'text-center mb-16',
                children: [
                  { type: 'h2', className: 'text-3xl md:text-4xl font-bold text-gray-900', children: [t('Meet Our Team')] },
                  { type: 'p', className: 'text-gray-500 mt-4 text-lg', children: [t('The passionate people behind every design')] }
                ]
              },
              {
                type: 'div',
                className: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10',
                children: [
                  {
                    type: 'div',
                    className: 'text-center group',
                    children: [
                      {
                        type: 'div',
                        className: 'w-40 h-40 mx-auto mb-6 rounded-full overflow-hidden bg-gray-100',
                        children: [
                          { type: 'img', className: 'w-full h-full object-cover', attributes: { src: PH_SQ, alt: 'Team member' } }
                        ]
                      },
                      { type: 'h3', className: 'text-lg font-bold text-gray-900', children: [t('Your Name')] },
                      { type: 'p', className: 'text-indigo-500 font-medium text-sm mt-1', children: [t('Founder & Creative Director')] },
                      { type: 'p', className: 'text-gray-500 text-sm mt-3 max-w-xs mx-auto', children: [t('Passionate about merging traditional craftsmanship with contemporary design aesthetics.')] }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'text-center group',
                    children: [
                      {
                        type: 'div',
                        className: 'w-40 h-40 mx-auto mb-6 rounded-full overflow-hidden bg-gray-100',
                        children: [
                          { type: 'img', className: 'w-full h-full object-cover', attributes: { src: PH_SQ, alt: 'Team member' } }
                        ]
                      },
                      { type: 'h3', className: 'text-lg font-bold text-gray-900', children: [t('Team Member')] },
                      { type: 'p', className: 'text-indigo-500 font-medium text-sm mt-1', children: [t('Head of Design')] },
                      { type: 'p', className: 'text-gray-500 text-sm mt-3 max-w-xs mx-auto', children: [t('Bringing fresh perspectives and innovative techniques to every collection we create.')] }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'text-center group',
                    children: [
                      {
                        type: 'div',
                        className: 'w-40 h-40 mx-auto mb-6 rounded-full overflow-hidden bg-gray-100',
                        children: [
                          { type: 'img', className: 'w-full h-full object-cover', attributes: { src: PH_SQ, alt: 'Team member' } }
                        ]
                      },
                      { type: 'h3', className: 'text-lg font-bold text-gray-900', children: [t('Team Member')] },
                      { type: 'p', className: 'text-indigo-500 font-medium text-sm mt-1', children: [t('Production Manager')] },
                      { type: 'p', className: 'text-gray-500 text-sm mt-3 max-w-xs mx-auto', children: [t('Ensuring every garment meets our exacting quality standards from fabric to finish.')] }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // 7. STATS — KEY METRICS
  // ═══════════════════════════════════════════════════════════════
  {
    category: 'Stats',
    label: 'Key Numbers',
    description: 'Bold statistics in a dark-themed row',
    block: {
      id: 'about_stats',
      type: 'section',
      enabled: true,
      structure: {
        type: 'section',
        className: 'py-20 bg-gray-900 text-white',
        children: [
          {
            type: 'div',
            className: 'max-w-6xl mx-auto px-6',
            children: [
              {
                type: 'div',
                className: 'grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 text-center',
                children: [
                  {
                    type: 'div',
                    children: [
                      { type: 'p', className: 'text-4xl md:text-5xl font-bold text-white', children: [t('500+')] },
                      { type: 'p', className: 'text-gray-400 mt-2 font-medium', children: [t('Designs Created')] }
                    ]
                  },
                  {
                    type: 'div',
                    children: [
                      { type: 'p', className: 'text-4xl md:text-5xl font-bold text-white', children: [t('10K+')] },
                      { type: 'p', className: 'text-gray-400 mt-2 font-medium', children: [t('Happy Customers')] }
                    ]
                  },
                  {
                    type: 'div',
                    children: [
                      { type: 'p', className: 'text-4xl md:text-5xl font-bold text-white', children: [t('15+')] },
                      { type: 'p', className: 'text-gray-400 mt-2 font-medium', children: [t('Collections')] }
                    ]
                  },
                  {
                    type: 'div',
                    children: [
                      { type: 'p', className: 'text-4xl md:text-5xl font-bold text-white', children: [t('5+')] },
                      { type: 'p', className: 'text-gray-400 mt-2 font-medium', children: [t('Years of Craft')] }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // 8. TIMELINE — JOURNEY
  // ═══════════════════════════════════════════════════════════════
  {
    category: 'Timeline',
    label: 'Our Journey',
    description: 'Vertical timeline with milestones',
    block: {
      id: 'about_timeline',
      type: 'section',
      enabled: true,
      structure: {
        type: 'section',
        className: 'py-20 bg-white',
        children: [
          {
            type: 'div',
            className: 'max-w-3xl mx-auto px-6',
            children: [
              {
                type: 'h2',
                className: 'text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16',
                children: [t('Our Journey')]
              },
              {
                type: 'div',
                className: 'relative',
                style: { paddingLeft: '32px', borderLeft: '2px solid #e5e7eb' },
                children: [
                  {
                    type: 'div',
                    className: 'mb-12 relative',
                    children: [
                      {
                        type: 'div',
                        className: 'absolute -left-[41px] w-4 h-4 rounded-full bg-indigo-500 border-4 border-white',
                        children: []
                      },
                      { type: 'p', className: 'text-sm font-bold text-indigo-500 mb-1', children: [t('2020')] },
                      { type: 'h3', className: 'text-xl font-bold text-gray-900 mb-2', children: [t('The Beginning')] },
                      { type: 'p', className: 'text-gray-600 leading-relaxed', children: [t('Started with a vision and a sewing machine. Our first collection featured just 10 pieces, each handcrafted with love.')] }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'mb-12 relative',
                    children: [
                      {
                        type: 'div',
                        className: 'absolute -left-[41px] w-4 h-4 rounded-full bg-indigo-500 border-4 border-white',
                        children: []
                      },
                      { type: 'p', className: 'text-sm font-bold text-indigo-500 mb-1', children: [t('2021')] },
                      { type: 'h3', className: 'text-xl font-bold text-gray-900 mb-2', children: [t('First Collection Launch')] },
                      { type: 'p', className: 'text-gray-600 leading-relaxed', children: [t('Launched our debut collection to incredible reception. Featured in multiple fashion publications and style guides.')] }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'mb-12 relative',
                    children: [
                      {
                        type: 'div',
                        className: 'absolute -left-[41px] w-4 h-4 rounded-full bg-indigo-500 border-4 border-white',
                        children: []
                      },
                      { type: 'p', className: 'text-sm font-bold text-indigo-500 mb-1', children: [t('2023')] },
                      { type: 'h3', className: 'text-xl font-bold text-gray-900 mb-2', children: [t('Expanding Horizons')] },
                      { type: 'p', className: 'text-gray-600 leading-relaxed', children: [t('Expanded our team and product range. Introduced sustainable materials and ethical manufacturing processes across all lines.')] }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'relative',
                    children: [
                      {
                        type: 'div',
                        className: 'absolute -left-[41px] w-4 h-4 rounded-full bg-indigo-500 border-4 border-white',
                        children: []
                      },
                      { type: 'p', className: 'text-sm font-bold text-indigo-500 mb-1', children: [t('Today')] },
                      { type: 'h3', className: 'text-xl font-bold text-gray-900 mb-2', children: [t('Where We Are Now')] },
                      { type: 'p', className: 'text-gray-600 leading-relaxed', children: [t('Serving thousands of customers worldwide with a growing collection of designs that celebrate individuality and conscious fashion.')] }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // 9. VALUES — CORE VALUES
  // ═══════════════════════════════════════════════════════════════
  {
    category: 'Values',
    label: 'Core Values',
    description: 'Grid of values with icons and descriptions',
    block: {
      id: 'about_values',
      type: 'section',
      enabled: true,
      structure: {
        type: 'section',
        className: 'py-20 bg-gray-50',
        children: [
          {
            type: 'div',
            className: 'max-w-6xl mx-auto px-6',
            children: [
              {
                type: 'div',
                className: 'text-center mb-16',
                children: [
                  { type: 'h2', className: 'text-3xl md:text-4xl font-bold text-gray-900', children: [t('Our Core Values')] },
                  { type: 'p', className: 'text-gray-500 mt-4 text-lg max-w-2xl mx-auto', children: [t('The principles that guide everything we create')] }
                ]
              },
              {
                type: 'div',
                className: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8',
                children: [
                  {
                    type: 'div',
                    className: 'p-8 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow',
                    children: [
                      { type: 'span', className: 'text-3xl', children: [t('\uD83C\uDF3F')] },
                      { type: 'h3', className: 'text-lg font-bold text-gray-900 mt-4 mb-3', children: [t('Sustainability')] },
                      { type: 'p', className: 'text-gray-600 leading-relaxed', children: [t('We choose eco-friendly materials and ethical processes, because fashion should never cost the earth.')] }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'p-8 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow',
                    children: [
                      { type: 'span', className: 'text-3xl', children: [t('\u2764\uFE0F')] },
                      { type: 'h3', className: 'text-lg font-bold text-gray-900 mt-4 mb-3', children: [t('Craftsmanship')] },
                      { type: 'p', className: 'text-gray-600 leading-relaxed', children: [t('Every stitch is intentional. We pour our heart into each piece, ensuring quality you can feel.')] }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'p-8 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow',
                    children: [
                      { type: 'span', className: 'text-3xl', children: [t('\uD83D\uDCA1')] },
                      { type: 'h3', className: 'text-lg font-bold text-gray-900 mt-4 mb-3', children: [t('Innovation')] },
                      { type: 'p', className: 'text-gray-600 leading-relaxed', children: [t('We push boundaries, experiment with new techniques, and reimagine what fashion can be.')] }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'p-8 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow',
                    children: [
                      { type: 'span', className: 'text-3xl', children: [t('\uD83E\uDD1D')] },
                      { type: 'h3', className: 'text-lg font-bold text-gray-900 mt-4 mb-3', children: [t('Inclusivity')] },
                      { type: 'p', className: 'text-gray-600 leading-relaxed', children: [t('Fashion is for everyone. We design for all bodies, all occasions, and all expressions of self.')] }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'p-8 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow',
                    children: [
                      { type: 'span', className: 'text-3xl', children: [t('\u2B50')] },
                      { type: 'h3', className: 'text-lg font-bold text-gray-900 mt-4 mb-3', children: [t('Excellence')] },
                      { type: 'p', className: 'text-gray-600 leading-relaxed', children: [t('Good enough is never enough. We strive for perfection in every detail, from design to delivery.')] }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'p-8 bg-white rounded-2xl shadow-sm hover:shadow-md transition-shadow',
                    children: [
                      { type: 'span', className: 'text-3xl', children: [t('\uD83C\uDF0D')] },
                      { type: 'h3', className: 'text-lg font-bold text-gray-900 mt-4 mb-3', children: [t('Community')] },
                      { type: 'p', className: 'text-gray-600 leading-relaxed', children: [t('We\'re more than a brand \u2014 we\'re a community of creators, dreamers, and changemakers.')] }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // 10. GALLERY — PHOTO GRID
  // ═══════════════════════════════════════════════════════════════
  {
    category: 'Gallery',
    label: 'Photo Gallery',
    description: 'Responsive image grid for behind-the-scenes',
    block: {
      id: 'about_gallery',
      type: 'section',
      enabled: true,
      structure: {
        type: 'section',
        className: 'py-20 bg-white',
        children: [
          {
            type: 'div',
            className: 'max-w-6xl mx-auto px-6',
            children: [
              {
                type: 'div',
                className: 'text-center mb-12',
                children: [
                  { type: 'h2', className: 'text-3xl md:text-4xl font-bold text-gray-900', children: [t('Behind the Scenes')] },
                  { type: 'p', className: 'text-gray-500 mt-4 text-lg', children: [t('A glimpse into our creative process')] }
                ]
              },
              {
                type: 'div',
                className: 'grid grid-cols-2 md:grid-cols-3 gap-4',
                children: [
                  {
                    type: 'div',
                    className: 'aspect-square overflow-hidden rounded-xl bg-gray-100',
                    children: [
                      { type: 'img', className: 'w-full h-full object-cover hover:scale-105 transition-transform duration-500', attributes: { src: PH_SQ, alt: 'Gallery image 1' } }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'aspect-square overflow-hidden rounded-xl bg-gray-100',
                    children: [
                      { type: 'img', className: 'w-full h-full object-cover hover:scale-105 transition-transform duration-500', attributes: { src: PH_SQ, alt: 'Gallery image 2' } }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'aspect-square overflow-hidden rounded-xl bg-gray-100',
                    children: [
                      { type: 'img', className: 'w-full h-full object-cover hover:scale-105 transition-transform duration-500', attributes: { src: PH_SQ, alt: 'Gallery image 3' } }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'aspect-square overflow-hidden rounded-xl bg-gray-100',
                    children: [
                      { type: 'img', className: 'w-full h-full object-cover hover:scale-105 transition-transform duration-500', attributes: { src: PH_SQ, alt: 'Gallery image 4' } }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'aspect-square overflow-hidden rounded-xl bg-gray-100',
                    children: [
                      { type: 'img', className: 'w-full h-full object-cover hover:scale-105 transition-transform duration-500', attributes: { src: PH_SQ, alt: 'Gallery image 5' } }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'aspect-square overflow-hidden rounded-xl bg-gray-100',
                    children: [
                      { type: 'img', className: 'w-full h-full object-cover hover:scale-105 transition-transform duration-500', attributes: { src: PH_SQ, alt: 'Gallery image 6' } }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // 11. QUOTE — FEATURED
  // ═══════════════════════════════════════════════════════════════
  {
    category: 'Content',
    label: 'Featured Quote',
    description: 'Large centered quote with attribution',
    block: {
      id: 'about_quote',
      type: 'section',
      enabled: true,
      structure: {
        type: 'section',
        className: 'py-24 md:py-32 bg-gray-50',
        children: [
          {
            type: 'div',
            className: 'max-w-4xl mx-auto text-center px-6',
            children: [
              {
                type: 'span',
                className: 'text-7xl md:text-8xl leading-none text-indigo-200 font-serif',
                children: [t('\u201C')]
              },
              {
                type: 'p',
                className: 'text-2xl md:text-3xl font-medium text-gray-800 mt-4 mb-10 leading-relaxed',
                style: { fontStyle: 'italic' },
                children: [t('Fashion is the armor to survive the reality of everyday life. Our designs are more than clothes \u2014 they are a statement of who you are and who you aspire to be.')]
              },
              {
                type: 'div',
                className: 'w-12 h-px bg-gray-300 mx-auto mb-6',
                children: []
              },
              {
                type: 'p',
                className: 'font-semibold text-gray-900 text-lg',
                children: [t('Your Name')]
              },
              {
                type: 'p',
                className: 'text-sm text-gray-500 mt-1',
                children: [t('Founder & Creative Director')]
              }
            ]
          }
        ]
      }
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // 12. CTA — CALL TO ACTION
  // ═══════════════════════════════════════════════════════════════
  {
    category: 'CTA',
    label: 'Call to Action',
    description: 'Bold gradient banner with CTA button',
    block: {
      id: 'about_cta',
      type: 'section',
      enabled: true,
      structure: {
        type: 'section',
        className: 'py-20 text-white',
        style: { background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)' },
        children: [
          {
            type: 'div',
            className: 'max-w-4xl mx-auto text-center px-6',
            children: [
              {
                type: 'h2',
                className: 'text-3xl md:text-4xl font-bold mb-6',
                children: [t('Let\u2019s Create Something Beautiful')]
              },
              {
                type: 'p',
                className: 'text-lg text-indigo-100 mb-10 max-w-2xl mx-auto leading-relaxed',
                children: [t('Ready to find your perfect style? Explore our latest collection or get in touch for custom designs tailored just for you.')]
              },
              {
                type: 'div',
                className: 'flex flex-col sm:flex-row gap-4 justify-center',
                children: [
                  {
                    type: 'a',
                    className: 'inline-block px-8 py-4 bg-white text-indigo-600 font-semibold rounded-full hover:shadow-lg transition-all',
                    attributes: { href: '/shop' },
                    children: [t('Shop Collection')]
                  },
                  {
                    type: 'a',
                    className: 'inline-block px-8 py-4 border-2 border-white text-white font-semibold rounded-full hover:bg-white hover:text-indigo-600 transition-all',
                    attributes: { href: '/contact' },
                    children: [t('Contact Us')]
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // 13. PROCESS — HOW WE WORK
  // ═══════════════════════════════════════════════════════════════
  {
    category: 'Process',
    label: 'Our Process',
    description: 'Step-by-step process with numbered cards',
    block: {
      id: 'about_process',
      type: 'section',
      enabled: true,
      structure: {
        type: 'section',
        className: 'py-20 bg-white',
        children: [
          {
            type: 'div',
            className: 'max-w-6xl mx-auto px-6',
            children: [
              {
                type: 'div',
                className: 'text-center mb-16',
                children: [
                  { type: 'h2', className: 'text-3xl md:text-4xl font-bold text-gray-900', children: [t('How We Work')] },
                  { type: 'p', className: 'text-gray-500 mt-4 text-lg', children: [t('From concept to creation')] }
                ]
              },
              {
                type: 'div',
                className: 'grid grid-cols-1 md:grid-cols-4 gap-8',
                children: [
                  {
                    type: 'div',
                    className: 'text-center',
                    children: [
                      {
                        type: 'div',
                        className: 'w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-5',
                        children: [
                          { type: 'span', className: 'text-xl font-bold text-indigo-600', children: [t('01')] }
                        ]
                      },
                      { type: 'h3', className: 'text-lg font-bold text-gray-900 mb-3', children: [t('Inspiration')] },
                      { type: 'p', className: 'text-gray-500 text-sm leading-relaxed', children: [t('We draw inspiration from art, culture, nature, and the world around us to envision each collection.')] }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'text-center',
                    children: [
                      {
                        type: 'div',
                        className: 'w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-5',
                        children: [
                          { type: 'span', className: 'text-xl font-bold text-indigo-600', children: [t('02')] }
                        ]
                      },
                      { type: 'h3', className: 'text-lg font-bold text-gray-900 mb-3', children: [t('Design')] },
                      { type: 'p', className: 'text-gray-500 text-sm leading-relaxed', children: [t('Our designers sketch, drape, and prototype until every line, curve, and detail is perfect.')] }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'text-center',
                    children: [
                      {
                        type: 'div',
                        className: 'w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-5',
                        children: [
                          { type: 'span', className: 'text-xl font-bold text-indigo-600', children: [t('03')] }
                        ]
                      },
                      { type: 'h3', className: 'text-lg font-bold text-gray-900 mb-3', children: [t('Craft')] },
                      { type: 'p', className: 'text-gray-500 text-sm leading-relaxed', children: [t('Skilled artisans bring designs to life using premium materials and time-honored techniques.')] }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'text-center',
                    children: [
                      {
                        type: 'div',
                        className: 'w-14 h-14 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-5',
                        children: [
                          { type: 'span', className: 'text-xl font-bold text-indigo-600', children: [t('04')] }
                        ]
                      },
                      { type: 'h3', className: 'text-lg font-bold text-gray-900 mb-3', children: [t('Deliver')] },
                      { type: 'p', className: 'text-gray-500 text-sm leading-relaxed', children: [t('Each piece is carefully finished, quality-checked, and beautifully packaged before reaching you.')] }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // 14. CRAFTSMANSHIP — IMAGE + TEXT
  // ═══════════════════════════════════════════════════════════════
  {
    category: 'Content',
    label: 'Craftsmanship Showcase',
    description: 'Full-width image with text overlay',
    block: {
      id: 'about_craftsmanship',
      type: 'section',
      enabled: true,
      structure: {
        type: 'section',
        className: 'bg-gray-900 text-white',
        children: [
          {
            type: 'div',
            className: 'grid grid-cols-1 lg:grid-cols-2',
            children: [
              {
                type: 'div',
                className: 'relative min-h-[400px] overflow-hidden',
                children: [
                  { type: 'img', className: 'w-full h-full object-cover absolute inset-0', attributes: { src: PH, alt: 'Craftsmanship' } }
                ]
              },
              {
                type: 'div',
                className: 'flex items-center px-8 lg:px-16 py-16',
                children: [
                  {
                    type: 'div',
                    children: [
                      { type: 'p', className: 'text-sm uppercase tracking-widest text-gray-400 mb-4', style: { letterSpacing: '0.2em' }, children: [t('Craftsmanship')] },
                      { type: 'h2', className: 'text-3xl md:text-4xl font-bold mb-6', children: [t('Crafted with Care, Made to Last')] },
                      { type: 'p', className: 'text-gray-300 leading-relaxed mb-8 text-lg', children: [t('Every piece in our collection is a testament to meticulous craftsmanship. From hand-selected fabrics to precision tailoring, we ensure nothing less than perfection.')] },
                      {
                        type: 'div',
                        className: 'grid grid-cols-2 gap-6',
                        children: [
                          {
                            type: 'div',
                            children: [
                              { type: 'p', className: 'text-2xl font-bold text-white', children: [t('100%')] },
                              { type: 'p', className: 'text-sm text-gray-400 mt-1', children: [t('Premium Fabrics')] }
                            ]
                          },
                          {
                            type: 'div',
                            children: [
                              { type: 'p', className: 'text-2xl font-bold text-white', children: [t('Hand')] },
                              { type: 'p', className: 'text-sm text-gray-400 mt-1', children: [t('Finished Details')] }
                            ]
                          },
                          {
                            type: 'div',
                            children: [
                              { type: 'p', className: 'text-2xl font-bold text-white', children: [t('Ethical')] },
                              { type: 'p', className: 'text-sm text-gray-400 mt-1', children: [t('Production')] }
                            ]
                          },
                          {
                            type: 'div',
                            children: [
                              { type: 'p', className: 'text-2xl font-bold text-white', children: [t('Zero')] },
                              { type: 'p', className: 'text-sm text-gray-400 mt-1', children: [t('Waste Design')] }
                            ]
                          }
                        ]
                      }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },

  // ═══════════════════════════════════════════════════════════════
  // 15. CONTACT — INFO SECTION
  // ═══════════════════════════════════════════════════════════════
  {
    category: 'Contact',
    label: 'Contact Information',
    description: 'Elegant contact section with email, phone, and address',
    block: {
      id: 'about_contact',
      type: 'section',
      enabled: true,
      structure: {
        type: 'section',
        className: 'py-20 bg-gray-50',
        children: [
          {
            type: 'div',
            className: 'max-w-4xl mx-auto px-6 text-center',
            children: [
              {
                type: 'h2',
                className: 'text-3xl md:text-4xl font-bold text-gray-900 mb-4',
                children: [t('Get in Touch')]
              },
              {
                type: 'p',
                className: 'text-gray-500 mb-12 text-lg',
                children: [t('We\u2019d love to hear from you. Reach out anytime.')]
              },
              {
                type: 'div',
                className: 'grid grid-cols-1 md:grid-cols-3 gap-8',
                children: [
                  {
                    type: 'div',
                    className: 'p-8 bg-white rounded-2xl shadow-sm',
                    children: [
                      { type: 'span', className: 'text-3xl', children: [t('\u2709\uFE0F')] },
                      { type: 'h3', className: 'text-lg font-bold text-gray-900 mt-4 mb-2', children: [t('Email')] },
                      { type: 'p', className: 'text-gray-600', children: [t('hello@yourbrand.com')] }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'p-8 bg-white rounded-2xl shadow-sm',
                    children: [
                      { type: 'span', className: 'text-3xl', children: [t('\uD83D\uDCDE')] },
                      { type: 'h3', className: 'text-lg font-bold text-gray-900 mt-4 mb-2', children: [t('Phone')] },
                      { type: 'p', className: 'text-gray-600', children: [t('+91 98765 43210')] }
                    ]
                  },
                  {
                    type: 'div',
                    className: 'p-8 bg-white rounded-2xl shadow-sm',
                    children: [
                      { type: 'span', className: 'text-3xl', children: [t('\uD83D\uDCCD')] },
                      { type: 'h3', className: 'text-lg font-bold text-gray-900 mt-4 mb-2', children: [t('Studio')] },
                      { type: 'p', className: 'text-gray-600', children: [t('Your City, Your State, India')] }
                    ]
                  }
                ]
              }
            ]
          }
        ]
      }
    }
  },
];

export default aboutSectionBlocks;
